<script setup lang="ts">
import {onBeforeMount, onMounted} from "vue";
import Translator from "@/utils/common/Translator";
import LoginUtils from "@/utils/login/LoginUtils";
import NamaSpinner from "@/components/NamaSpinner.vue";
import {useLoginInfoStore} from "@/stores/loginInfo";
import Auth from "@/views/Auth.vue";
import {useLoadingStore} from "@/stores/LoadingStore";
import {useToastManager} from "@/stores/ToastsComposable";
import AutoLogoutTime from "@/utils/login/AutoLogoutTime";
import langEn from 'quasar/lang/en-US.js'
import langAr from 'quasar/lang/ar.js'
import {Quasar, Screen, setCssVar} from "quasar";
import {useErrorsStore, useNotificationsStore} from "@/stores/ToastDataStore";
import Home from "@/views/Home.vue";
import DefaultApplicationInfo from "@/utils/common/DefaultApplicationInfo.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {modifyJSPrototypes} from "@/utils/common/NamaPrototypeExtensions.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import NamaActionsSpinner from "@/components/NamaActionsSpinner.vue";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {provideGlobalGridOptions, themeQuartz} from "ag-grid-enterprise";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";
import DialogUtils from "@/utils/common/DialogUtils.ts";
import FieldInfoUtils from "@/utils/common/FieldInfoUtils.ts";
import UpdateTranslationsOfAnEnum from "@/components/dialogs/UpdateTranslationsOfAnEnum.vue";
import TopPanelSearchUtils from "@/utils/common/TopPanelSearchUtils.ts";

AGGridUtils.registerGridModules();
modifyJSPrototypes();

const loadingStore = useLoadingStore();
const loginInfoStore = useLoginInfoStore();
let themeStore = useThemeStore();

LoginUtils.RESET_PASSWORD_CALLBACK = async () => {
  await DialogUtils.showPasswordMustBeChangedDialog();
  return;
}
FieldInfoUtils.UpdateTranslationsDialogIndirectComponent = UpdateTranslationsOfAnEnum;
function initLocale() {
  Translator.initLocale();
  if (Translator.isLanguageRTL()) {
    document.documentElement.dir = 'rtl';
  }
}

function initTheme() {
  document.documentElement.style.setProperty('--nama-primary-light', themeStore.getPrimaryColor());
  document.documentElement.style.setProperty('--nama-primary-dark', themeStore.getPrimaryDarkColor());
  document.documentElement.style.setProperty('--nama-background-bright-light', themeStore.getBackgroundBrightLightColor());
  document.documentElement.style.setProperty('--nama-background-bright-dark', themeStore.getBackgroundBrightDarkColor());
  document.documentElement.style.setProperty('--nama-background-deep-light', themeStore.getBackgroundDeepLightColor());
  document.documentElement.style.setProperty('--nama-background-deep-dark', themeStore.getBackgroundDeepDarkColor());
  document.documentElement.style.setProperty('--nama-background-dim-light', themeStore.getBackgroundDimLightColor());
  document.documentElement.style.setProperty('--nama-background-dim-dark', themeStore.getBackgroundDimDarkColor());
  document.documentElement.style.setProperty('--nama-warning-light', themeStore.getWarningLightColor());
  document.documentElement.style.setProperty('--nama-warning-dark', themeStore.getWarningDarkColor());
  document.documentElement.style.setProperty('--nama-font-light', themeStore.getFontLightColor());
  document.documentElement.style.setProperty('--nama-font-dark', themeStore.getFontDarkColor());
  document.documentElement.style.setProperty('--nama-font-size', themeStore.getFontSize());
  useNamaFlagsStore().darkMode ? document.documentElement.classList.add('tw-dark') : document.documentElement.classList.remove('tw-dark');
  useNamaFlagsStore().horizontalMode ? document.documentElement.classList.add('tw-horizontal') : document.documentElement.classList.remove('tw-horizontal');
  useNamaFlagsStore().quadLayout ? document.documentElement.classList.add('tw-quad') : document.documentElement.classList.remove('tw-quad');
  const dm = useNamaFlagsStore().densityMode;
  if (dm === 'Compact')
    document.documentElement.classList.add('tw-compact');
  else if (dm === 'Ultra')
    document.documentElement.classList.add('tw-ultra');
  AGGridUtils.updateThemeVars()
  provideGlobalGridOptions({
    theme: themeQuartz.withParams(AGGridUtils.defaultAGGridThemeOptions)
  });
}

async function initLogin() {
  await LoginUtils.tryLoginFromSession();
}

async function initApp() {
  initLocale();
  Screen.setSizes({sm: 1025, md: 1100, lg: 1300, xl: 1400})
  langAr.date.monthsShort = langAr.date.months;
  Quasar.lang.set(Translator.isLanguageRTL() ? langAr : langEn);
  setCssVar('primary', useNamaFlagsStore().darkMode ? themeStore.getPrimaryDarkColor() : themeStore.getPrimaryColor());
  await themeStore.fetchAndApplyTheme();
  initTheme();
  GUIUtilities.initWindowFunctions();
  AutoLogoutTime.registerLoginLogoutEvents();
  // initToggleHeaders(); //mainapp.js ::TODO:: toggle Groups save to localstorage
  await initLogin();
}

onBeforeMount(async () => {
  loadingStore.startFirstTimeLoading();
  await initApp();
  useToastManager();
  if (loginInfoStore.isLoggedIn)
    useNotificationsStore().pushNotifications(loginInfoStore.notifications ?? []);
  if (DefaultApplicationInfo.applicationVersion() !== window['serverAppVersion'] && import.meta.env.PROD)
    useErrorsStore().pushErrorStr(Translator.translateId("applicationVersionIsInvalid"));
  loadingStore.stopFirstTimeLoading();
})
onMounted(() => {
  let wnd = window as any;
  wnd.waitForLoadingIndicators = async () => {
    await loadingStore.awaitLoadingFinish(10);
  };
  wnd.focusOnField = async (fieldId: string, dontFlash?: boolean) => {
    const fields = TopPanelSearchUtils.searchForFields(fieldId)

    if (!fields || !fields.length)
      return "No Field Found";
    useNamaFlagsStore().setFieldToFocusOptions({field: fields[0], dontFlash,});
    return "Found field, setting focus";
  };
  wnd.isLoggedIn = () => {
    return loginInfoStore.isLoggedIn;
  }
});

</script>

<template>
  <NamaActionsSpinner v-if="loadingStore.isLoadingActive"/>
  <NamaSpinner v-if="loadingStore.isFirstTimeLoadingActive"/>
  <Auth v-else-if="!loginInfoStore.isLoggedIn"/>
  <keep-alive>
    <Home v-if="loginInfoStore.isLoggedIn" :key="LoginUtils.loginInfo?.userInfo?.id"/>
  </keep-alive>
</template>

<style scoped>
</style>

